import React from 'react';
import { I18n } from '@fiverr-private/i18n-react';
import { useGigPageContext } from '@fiverr-private/gig_page_context';
import { VettedByProBanner } from '@fiverr-private/gig_page_banners';
import { ReviewsSnippet } from '@fiverr-private/gig_reviews';
import type { Review, ReviewAttributes, ReviewsSnippet as ReviewsSnippetType } from '@fiverr-private/gig_page_types';
import { ABOUT_SELLER, RECOMMENDATIONS, Waypoint } from '@fiverr-private/gig_page_waypoint_navigation';
import { AISummary } from '@fiverr-private/gig_seller_card';
import { GIG_SUB_HEADER } from '../../../utils/events';
import Description from '../../Description';
import Workflow from '../../Workflow';
import WorkProcess from '../../WorkProcess';
import Faq from '../../Faq';
import Reviews from '../../Reviews';
import Tags from '../../Tags';
import PackagesTable from '../../PackagesTable';
import OrderDetails from '../../OrderDetails';
import Gallery from '../../Gallery';
import Overview from '../../Overview';
import Portfolio from '../../Portfolio';
import { OutOfOffice } from '../../OutOfOffice/OutOfOffice';
import { Disclaimer } from '../../Disclaimer/Disclaimer';
import { MoreServices } from '../../MoreServices/MoreServices';
import UGCTranslateButton from '../../UGCTranslateButton';
import LimitedEditionBanner from '../../LimitedEdition/Banner';
import PersonalizationWrapper from '../../PersonalizationWrapper';
import VoiceOverAiAudition from '../../VoiceOverAiAudition';
import SellerTranslationBadgeWrapper from '../../shared/SellerTranslationBadgeWrapper';
import { SECTION_NAMES } from '../../PersonalizationWrapper/constants';
import { LayoutProps } from '../gigThemesConfig';
import { SellerCard } from '../../SellerCard/SellerCard';
import { UpmarketBanners } from '../../UpmarketBanners';

const calculateAboutSellerTitleKey = (isAgency: boolean, isBusinessLayout: boolean) => {
  if (isBusinessLayout) {
    return isAgency ? 'gig_page_perseus.general.about_us' : 'gig_page_perseus.general.about_me';
  }

  return isAgency ? 'gig_page_perseus.general.about_this_agency' : 'gig_page_perseus.general.about_the_seller_new';
};

export const ExperientialGigPage = (props: LayoutProps) => {
  const {
    overview,
    repeatScore,
    notableClients,
    voiceOverAiAudition,
    gallery,
    reviews,
    workflow,
    seller,
    tags,
    info,
    description,
    shouldRenderUGCTranslateButton,
    username,
    portfolioProps,
    useStickyLayout,
    paymentType,
    shouldRenderComparePersonalizedSection,
    ugc,
    sellerCard,
    summary,
  } = props;

  const {
    isBusinessLayout,
    seller: { isAgency },
    gig: { isRestrictedByRegion },
    general: { isLimitedEdition },
    biankaEvents: { reportAISummaryToggle, reportAISummaryImpression },
  } = useGigPageContext();
  const aboutTheSellerTitleKey = calculateAboutSellerTitleKey(isAgency, isBusinessLayout);

  const shouldRenderReviews = props.shouldRenderReviews();
  const upmarketBannerVariants = props.getUpmarketBannerVariants();
  const shouldShowAISummary = props.shouldShowAISummary();
  const shouldRenderLimitedEditionBanner = props.shouldRenderLimitedEditionBanner();
  const shouldRenderNewProBanner = props.shouldRenderNewProBanner();
  const { metadataAttributes: descriptionMetadata } = description;
  const sellerName = seller?.user?.profile?.displayName || username;
  const reviewsSnippetAttributes: ReviewAttributes = {
    reviews: (reviews as Record<string, Review[]>).reviews,
    reviewSnippets: (reviews as Record<string, ReviewsSnippetType[]>).reviews_snippet,
  };

  return (
    <>
      {shouldRenderLimitedEditionBanner && <LimitedEditionBanner />}
      <OutOfOffice />
      <Overview
        overview={overview}
        studioInfo={info}
        useStickyLayout={useStickyLayout}
        repeatScore={repeatScore}
        notableClients={notableClients}
        isRestrictedByRegion={isRestrictedByRegion}
      />
      {shouldRenderUGCTranslateButton && <UGCTranslateButton componentName={GIG_SUB_HEADER} />}

      {voiceOverAiAudition && <VoiceOverAiAudition voiceOverAiAudition={voiceOverAiAudition} />}

      <Gallery gallery={gallery} />
      {shouldShowAISummary && (
        <div className="ai-summary-wrapper">
          <AISummary summary={summary} onToggle={reportAISummaryToggle} onImpression={reportAISummaryImpression} />
        </div>
      )}
      <ReviewsSnippet reviewAttributes={reviewsSnippetAttributes} isLimitedEdition={isLimitedEdition} />

      {shouldRenderNewProBanner && <VettedByProBanner seller={{ displayName: sellerName, username }} />}

      <Description metadataAttributes={descriptionMetadata} />

      <Workflow workflow={workflow} />
      <WorkProcess paymentType={paymentType} />

      <Disclaimer />

      <>
        <Waypoint source={ABOUT_SELLER} />
        <h2 className="section-title about-the-seller">
          <span>
            <SellerTranslationBadgeWrapper>
              <I18n k={aboutTheSellerTitleKey} params={{ sellerName }} />
            </SellerTranslationBadgeWrapper>
          </span>
        </h2>
        <SellerCard
          seller={seller}
          notableClients={notableClients}
          sellerName={username}
          ugc={ugc}
          recentDelivery={sellerCard.recentDelivery}
          proSubCategories={sellerCard.proSubCategories}
        />
        <Portfolio {...portfolioProps} />
      </>

      <PackagesTable />
      <OrderDetails />

      <Waypoint source={RECOMMENDATIONS} />
      {shouldRenderComparePersonalizedSection() && (
        <PersonalizationWrapper sectionName={SECTION_NAMES.COMPARE} className="personalized-content-compare" />
      )}

      <MoreServices />
      <Faq />

      <UpmarketBanners upmarketBannerVariants={upmarketBannerVariants} />

      {shouldRenderReviews && <Reviews reviews={reviews} />}

      <Tags tags={tags} />
    </>
  );
};
